/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import { useEffect } from 'react';
import {
  BuildingType,
  CityStats,
  Grid,
  NewsItem,
  ResearchState,
  Scenario,
} from '../types';
import { enhancedObjectivesManager } from '../services/enhancedObjectivesManager';
import { ENHANCED_OBJECTIVES } from '../constants/enhancedObjectives';
import { architectureFeatures } from '../src/infrastructure/config/features';
import {
  executeEvaluateEnhancedObjectives,
  registerEnhancedObjectives,
} from '../src/adapters/ui/hooks/useEnhancedObjectivesEvaluation';

export interface UseEnhancedObjectivesIntegrationDeps {
  gameStarted: boolean;
  gameStatus: string;
  activeScenario: Scenario | null;
  stats: CityStats;
  grid: Grid;
  research: ResearchState;
  gameSpeed: number;
  addNewsItem: (item: NewsItem) => void;
}

function buildBuildingCounts(grid: Grid): Record<string, number> {
  const buildingCounts: Record<string, number> = {};
  grid.flat().forEach((cell) => {
    if (cell.buildingType !== BuildingType.None) {
      buildingCounts[cell.buildingType] = (buildingCounts[cell.buildingType] || 0) + 1;
    }
  });
  return buildingCounts;
}

function notificationToNewsItem(notification: {
  title: string;
  message: string;
  severity?: 'info' | 'success' | 'warning' | 'error';
}): NewsItem {
  return {
    id: Date.now().toString(),
    text: `${notification.title}: ${notification.message}`,
    type: notification.severity === 'error' ? 'negative' : 'positive',
  };
}

export function useEnhancedObjectivesIntegration({
  gameStarted,
  gameStatus,
  activeScenario,
  stats,
  grid,
  research,
  gameSpeed,
  addNewsItem,
}: UseEnhancedObjectivesIntegrationDeps) {
  useEffect(() => {
    if (!gameStarted || gameStatus !== 'playing' || activeScenario) return;

    if (architectureFeatures.useEnhancedObjectivesUseCase) {
      registerEnhancedObjectives(ENHANCED_OBJECTIVES);
      return;
    }

    ENHANCED_OBJECTIVES.forEach((objective) => {
      enhancedObjectivesManager.addObjective(objective);
    });

    enhancedObjectivesManager.setNotificationCallback((notification) => {
      addNewsItem(notificationToNewsItem(notification));
    });
  }, [gameStarted, gameStatus, activeScenario, addNewsItem]);

  useEffect(() => {
    if (!gameStarted || gameStatus !== 'playing' || activeScenario) return;

    const buildingCounts = buildBuildingCounts(grid);

    if (architectureFeatures.useEnhancedObjectivesUseCase) {
      const { notifications } = executeEvaluateEnhancedObjectives({
        stats,
        buildingCounts,
        researchCompletedTechs: research.completedTechs,
        gameSpeed,
        scenarioId: activeScenario?.id || null,
      });

      notifications.forEach((notification) => {
        addNewsItem(notificationToNewsItem(notification));
      });
      return;
    }

    const context = {
      currentStats: stats,
      buildings: buildingCounts,
      products: stats.activeProducts,
      research: research.completedTechs,
      gameSpeed,
      scenario: activeScenario?.id || null,
    };

    enhancedObjectivesManager.evaluateObjectives(context);
  }, [
    stats.day,
    stats.budget,
    stats.compliance,
    stats.threat,
    stats.activeProducts,
    research.completedTechs,
    gameStarted,
    gameStatus,
    activeScenario,
    grid,
    gameSpeed,
    addNewsItem,
  ]);
}